/**
 * Auditoria de aderência ao TPL
 * Aderência (%) = (Executadas / Planejadas) × 100
 */

import type { LstExecucao, LstStatus, LstTemplate } from '../types';
import { isTemplateDueOnDate } from './lstSchedule';
import { computeTplOeeMetrics } from './lstOeeMetrics';

export type TplAuditStatus = 'executado' | 'nao_executado' | 'parcial';

export type TplAdherenceBand = 'verde' | 'amarelo' | 'vermelho';

export interface TplRoutineAuditRow {
  templateId: string;
  title: string;
  planned: boolean;
  status: TplAuditStatus;
  lstStatus: LstStatus | null;
  date: string;
  isCritical: boolean;
}

export interface TplAdherenceSummary {
  planned: number;
  executed: number;
  partial: number;
  missed: number;
  adherencePct: number;
  band: TplAdherenceBand;
}

export function mapLstStatusToAudit(status: LstStatus | null): TplAuditStatus {
  if (!status || status === 'nao_realizado') return 'nao_executado';
  if (status === 'realizado_com_desvio') return 'parcial';
  if (status === 'processo_parado') return 'executado';
  return 'executado';
}

export function computeTplAdherence(
  templates: LstTemplate[],
  execucoes: LstExecucao[],
  dates: string[],
): TplAdherenceSummary {
  const oee = computeTplOeeMetrics(templates, execucoes, dates);
  return {
    planned: oee.counts.planned,
    executed: oee.counts.conforme,
    partial: oee.counts.desvio,
    missed: oee.counts.naoRealizado,
    adherencePct: oee.disciplinaPct,
    band: adherenceBand(oee.disciplinaPct),
  };
}

export function adherenceBand(pct: number): TplAdherenceBand {
  if (pct >= 90) return 'verde';
  if (pct >= 75) return 'amarelo';
  return 'vermelho';
}

export const TPL_ADHERENCE_BAND_LABELS: Record<TplAdherenceBand, string> = {
  verde: '≥ 90% — Excelente',
  amarelo: '75–89% — Atenção',
  vermelho: '< 75% — Crítico',
};

export const TPL_ADHERENCE_BAND_STYLES: Record<TplAdherenceBand, string> = {
  verde: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  amarelo: 'bg-amber-100 text-amber-800 border-amber-200',
  vermelho: 'bg-red-100 text-red-800 border-red-200',
};

export function buildRoutineAuditRows(
  templates: LstTemplate[],
  execucoes: LstExecucao[],
  date: string,
): TplRoutineAuditRow[] {
  return templates
    .filter((t) => t.active && isTemplateDueOnDate(t, date))
    .map((tpl) => {
      const ex = execucoes.find((e) => e.template_id === tpl.id && e.data === date);
      return {
        templateId: tpl.id,
        title: tpl.title,
        planned: true,
        status: mapLstStatusToAudit(ex?.status ?? null),
        lstStatus: ex?.status ?? null,
        date,
        isCritical: tpl.is_critical,
      };
    });
}

export interface TplLeaderRanking {
  userId: string;
  name: string;
  adherencePct: number;
  band: TplAdherenceBand;
  planned: number;
  executed: number;
}

export function rankLeadersByAdherence(
  leaders: Array<{ userId: string; name: string; templates: LstTemplate[]; execucoes: LstExecucao[] }>,
  dates: string[],
): TplLeaderRanking[] {
  return leaders
    .map((l) => {
      const summary = computeTplAdherence(l.templates, l.execucoes, dates);
      return {
        userId: l.userId,
        name: l.name,
        adherencePct: summary.adherencePct,
        band: summary.band,
        planned: summary.planned,
        executed: summary.executed,
      };
    })
    .sort((a, b) => b.adherencePct - a.adherencePct);
}
